import React, { useEffect, useState } from "react";
import { R6IonInput } from "./r6ionic/R6IonInput";
import { IonItem } from "@ionic/react";
import { RegisterOptions, useWatch } from "react-hook-form";
import { errorProps } from "../services/errorService";
import { UseListRenderer, UseListSearch } from "./hooks/autocomplete/types";
import { useIsLanguageJapanese } from "./hooks";
import { AutocompleteModal } from "../modals/AutocompleteModal";

interface AutocompleteInputProps {
    label: string;
    name: string;
    errors: any;
    ready: boolean;
    setValue: (name: any, value: any, options?: any) => void;
    testId: string;
    searchFn: UseListSearch<any>;
    renderFn: UseListRenderer<any>;
    rules?: RegisterOptions<any>;
    onFocus?: () => void;
    onBlur?: () => void;
    placeholder?: string;
    useFlex?: boolean;
    disabled?: boolean;
}

/**
 * Modal-launcher variant used for the single-value create-match fields
 * (competition, venue, home/away team). The field is a read-only input that
 * opens an AutocompleteModal on tap, so there is no keyboard/scroll/focus
 * coexistence on the form page (the iOS-only source of the old bugs).
 */
function ModalLauncherInput({
    label,
    name,
    errors,
    ready,
    setValue,
    testId,
    searchFn,
    renderFn,
    rules,
    placeholder,
    disabled = false,
}: AutocompleteInputProps) {
    const [showModal, setShowModal] = useState<boolean>(false);
    const value = useWatch({ name }) ?? "";
    const canOpen = !disabled && ready;

    return (
        <>
            <IonItem
                data-testid={testId}
                {...errorProps(name, errors)}
                lines="full"
            >
                <R6IonInput
                    disabled={disabled}
                    name={name}
                    rules={rules}
                    placeholder={placeholder}
                    readonly={true}
                    onClick={() => canOpen && setShowModal(true)}
                    className={"align-text-end"}
                    label={label}
                />
            </IonItem>
            {showModal && (
                <AutocompleteModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    title={label}
                    currentValue={value}
                    searchFn={searchFn}
                    renderFn={renderFn}
                    onCommit={(v) =>
                        setValue(name, v, {
                            shouldValidate: true,
                            shouldDirty: true,
                        })
                    }
                    testId={`${testId || name}-modal`}
                />
            )}
        </>
    );
}

/**
 * Inline-badge variant kept for the match-officials accordion (useFlex). This
 * is a different UX (tap a previously-used official to fill one field) and is
 * not the iOS keyboard-fight pain point, so it keeps the in-place dropdown.
 */
function InlineFlexInput({
    label,
    name,
    errors,
    ready,
    setValue,
    testId,
    searchFn,
    renderFn,
    onFocus,
    onBlur,
    rules,
    placeholder,
    disabled = false,
}: AutocompleteInputProps) {
    const MIN_SEARCH_CHARS = 3;
    // REFSIX-763: drive suggestion visibility from an explicit "show" flag that
    // is opened on focus and closed only on selection — NOT on the input's
    // blur. Tapping (or starting to scroll) a suggestion blurs the IonInput, so
    // hiding the dropdown on blur removed it before the tap's click could land
    // and made the list impossible to scroll. The team-sheet player autocomplete
    // (PlayerItemComponent) works for the same reason: it keeps the list shown
    // until a value is picked.
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
    const [suggestions, setSuggestions] = useState<any>(null);
    const value = useWatch({ name: name });

    useEffect(() => {
        if (!ready) {
            return;
        }

        // Debounce the search (not the input). The R6IonInput below has no
        // `debounce`, so the typed value commits to react-hook-form
        // immediately and the suggestion list still throttles here.
        const timer = setTimeout(() => {
            const res =
                value && value.length >= MIN_SEARCH_CHARS
                    ? searchFn(value)
                    : searchFn("");
            setSuggestions(res);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchFn, ready, value]);

    return (
        <>
            <IonItem
                data-testid={testId}
                {...errorProps(name, errors)}
                lines="full"
            >
                <R6IonInput
                    disabled={disabled}
                    name={name}
                    rules={rules}
                    onFocus={() => {
                        setShowSuggestions(true);
                        onFocus && onFocus();
                    }}
                    onBlur={() => {
                        // Intentionally do NOT hide the dropdown here — see the
                        // showSuggestions note above. It closes on selection.
                        onBlur && onBlur();
                    }}
                    placeholder={placeholder}
                    className={"align-text-end"}
                    label={label}
                />
            </IonItem>
            <div
                className={
                    !suggestions ||
                    !showSuggestions ||
                    (value &&
                        value.length >= 3 &&
                        Object.keys(suggestions).length === 0)
                        ? "autocomplete"
                        : "autocomplete open flex"
                }
            >
                {suggestions &&
                    renderFn(suggestions, (item) => {
                        // The badge fires onClick, which lands after the tap has
                        // already blurred the input — so the now-unfocused
                        // IonInput repaints the committed value correctly on iOS.
                        setShowSuggestions(false);
                        setValue(name, item, {
                            shouldValidate: true,
                            shouldDirty: true,
                        });
                    })}
            </div>
        </>
    );
}

function SimpleInput({
    label,
    name,
    errors,
    testId,
    rules,
}: AutocompleteInputProps) {
    return (
        <IonItem
            data-testid={testId}
            {...errorProps(name, errors)}
            lines="full"
            onClick={(e) => {
                // scroll segment into view on click
                if (e.target) {
                    const el = e.target as HTMLElement;
                    const ionContent = el.closest("ion-content") as HTMLIonContentElement | null;
                    if (ionContent) {
                        ionContent.getScrollElement().then((scrollEl) => {
                            const elRect = el.getBoundingClientRect();
                            const contentRect = ionContent.getBoundingClientRect();
                            const offsetFromContentTop = elRect.top - contentRect.top;
                            const newScrollTop = scrollEl.scrollTop + offsetFromContentTop;
                            ionContent.scrollToPoint(0, newScrollTop, 300);
                        });
                    }
                }
            }}
        >
            <R6IonInput
                name={name}
                rules={rules}
                debounce={500}
                className={"align-text-end"}
                label={label}
            />
        </IonItem>
    );
}

const AutocompleteSwitch = (props: AutocompleteInputProps) => {
    const isLocalJP = useIsLanguageJapanese();
    if (isLocalJP) {
        return <SimpleInput {...props} />;
    }
    if (props.useFlex) {
        return <InlineFlexInput {...props} />;
    }
    return <ModalLauncherInput {...props} />;
};
export default AutocompleteSwitch;
